import React, { useState, useEffect, useRef } from 'react'
import { Heading } from 'components/heading'
import { Input, InputGroup } from 'components/input'
import { Select } from 'components/select'
import { Divider } from 'components/divider'
import { Badge } from 'components/badge'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { gql, useLazyQuery, useMutation } from '@apollo/client'
import { t } from 'i18next'

import { Pagination, PaginationList, PaginationNext, PaginationPage, PaginationPrevious } from 'components/pagination'
import {
    Dropdown,
    DropdownButton,
    DropdownDivider,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
} from 'components/dropdown'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/table'
import { MagnifyingGlassIcon, EllipsisVerticalIcon, ArrowPathIcon } from '@heroicons/react/16/solid'
import { EyeIcon, EyeSlashIcon, ArrowLeftIcon, PencilIcon } from '@heroicons/react/20/solid'
import { ErrorDialog, SuccessDialog, ConfirmDialog } from 'components/custom/ResponseDialog'
import { ExclamationTriangleIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import { CaptainIcon, UsersIcon } from 'components/icons'

import { GET_USER_LIST, GET_ADMIN_USER_ROLES } from 'graphql/users/queries'
import { UPDATE_USER_STATUS, CREATE_ADMIN_USER } from 'graphql/users/mutations'
import { ImageDisplay } from 'components/custom/ImageDisplay'
import placeHolderImage from 'assets/images/users/profile_default.png'
import loadingImage from '../../assets/images/boat-loading.gif'
import { use } from 'react'
import { getPageRange } from 'utils/paginationHelper'

const tabs = [
    { name: 'Admin Users', key: 'admin', current: true },
    // { name: "App Users", key: "other", current: false },
]

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

export default function Users() {
    const navigate = useNavigate()
    const [activeTab, setActiveTab] = useState('admin')
    const [filterObj, setFilterObj] = useState({
        role: null,
        active: null,
    })
    const [searchString, setSearchString] = useState('')
    const searchStringRef = useRef(searchString)

    // Dialog Message
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [successMessage, setSuccessMessage] = useState('')
    const [showConfirmDialog, setShowConfirmDialog] = useState(false)
    const [confirmMessage, setConfirmMessage] = useState('')

    const [userList, setUserList] = useState(null)
    const [userCount, setUserCount] = useState(0)
    const [adminRoles, setAdminRoles] = useState([])
    // for Status update
    const [statusUserId, setStatusUserId] = useState('')
    const [userStatus, setUserStatus] = useState(false)
    // pagination
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const totalPages = Math.ceil(userCount / rowsPerPage)

    useEffect(() => {
        searchStringRef.current = searchString
    }, [searchString])

    const [getUserList, { loading: userListLoading, error: userListError }] = useLazyQuery(GET_USER_LIST, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onCompleted: data => {
            if (data.searchUser.success && data.searchUser.code === '200') {
                setUserList(data.searchUser.users)
                setUserCount(data.searchUser.total_count)
            }
        },
    })

    const [getAdminUserRoles, { loading: rolesLoading, error: rolesError }] = useLazyQuery(GET_ADMIN_USER_ROLES, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onCompleted: data => {
            if (data.getAdminUserRoles.success && data.getAdminUserRoles.code === '200') {
                setAdminRoles(data.getAdminUserRoles.roles)
            }
        },
    })

    useEffect(() => {
        console.log('selected page===', page)
        searchUsers(filterObj)
    }, [page, rowsPerPage])

    useEffect(() => {
        getAdminUserRoles()
    }, [])

    const handlePageChange = newPage => {
        console.log('new handlePageChange===', newPage)
        setPage(newPage)
    }

    // Search users User List
    const searchUsers = filter => {
        console.log('searchKey===', filter)
        console.log('searchStringRef===', searchStringRef.current)
        getUserList({
            variables: {
                searchUserId: null,
                userPool: activeTab,
                role: filter.role === 'All' || filter.role === '' ? null : filter.role,
                page: {
                    offset: page * rowsPerPage,
                    limit: rowsPerPage,
                },
                searchString: searchStringRef.current,
                isActive: filter.active,
            },
        })
    }

    // Update User Status
    const [updateUserStatus, { loading: statusUpdateLoading }] = useMutation(UPDATE_USER_STATUS, {
        onCompleted: data => {
            if (data?.updateUserStatus?.code === '200') {
                setSuccessMessage(data?.updateUserStatus?.message)
                setShowSucDialog(true)
                setTimeout(() => {
                    searchUsers(filterObj)
                    setShowSucDialog(false)
                }, 500)
            } else {
                setErrorMessage('Problem updating the user status. Please try again in a few moments.')
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setErrorMessage('Problem updating the user status. Please try again in a few moments.')
            setShowErrDialog(true)
        },
    })

    const onClickSearch = () => {
        setPage(0)
        setRowsPerPage(10)
        searchUsers(filterObj)
    }
    const onClickSearchClear = () => {
        setSearchString('') // Clear the search string
        console.log('onClickSearchClear===', filterObj)
        setFilterObj({
            role: null,
            active: null,
        })
        setPage(0)
        setRowsPerPage(10)
        setTimeout(() => {
            searchUsers({
                role: null,
                active: null,
            })
        }, 100)
    }
    const handleDisableUser = (userId, status) => {
        console.log('handleDisableUser===', userId, status)
        setStatusUserId(userId)
        setUserStatus(status)
        const msg = status ? 'enable' : 'disable'
        setConfirmMessage(`Are you sure you want to ${msg} this user?`)
        setShowConfirmDialog(true)
    }

    const onEnableDisableUser = (userid, status) => {
        updateUserStatus({
            variables: {
                userPool: activeTab,
                userId: userid,
                enabled: status,
                remarks: status + ' user',
            },
        })
    }

    const handleErrClose = () => {
        setShowErrDialog(false)
        setErrorMessage('')
    }
    const handleSucClose = () => {
        setShowSucDialog(false)
        setSuccessMessage('')
    }

    const handleConfirmPositive = () => {
        console.log('handleConfirmPositive===')
        onEnableDisableUser(statusUserId, userStatus)
        setShowConfirmDialog(false)
    }

    const handleConfirmNegative = () => {
        console.log('handleConfirmNegative===')
        setShowConfirmDialog(false)
    }

    //  MUtation
    const [createAdminUserInfo, { loading: createUserLoading }] = useMutation(CREATE_ADMIN_USER, {
        onCompleted: data => {
            if (data?.createAdminUser.code === '200' && data?.createAdminUser.success === true) {
                setSuccessMessage('Code sent successfully')
                setShowSucDialog(true)
                setTimeout(() => {
                    searchUsers(filterObj)
                    setShowSucDialog(false)
                }, 500)
            } else {
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setShowErrDialog(true)
        },
    })

    const handleResendCode = userId => {
        console.log('handleResendCode===', userId)
        const profile_info = userList.find(user => user.id === userId)
        console.log('profile_info===', profile_info)
        const formattedData = {
            additionalInfo: {
                job_title: null,
                document_number: null,
                document_type_code: null,
                document: null,
                profile_photo: profile_info.additional_info.profile_photo,
            },
            userRoles: profile_info.user_roles,
            user: {
                email: profile_info.email,
                family_name: profile_info.user_info.family_name,
                given_name: profile_info.user_info.given_name,
                locale: profile_info.user_info.locale,
                phone_number: profile_info.user_info.phone_number,
                resend_code: true,
            },
        }
        createAdminUserInfo({
            variables: formattedData,
        })
    }

    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => {
                        navigate('/')
                    }}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Home')}
                </button>
            </div>

            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <div>
                        <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap justify-between">
                            <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap">
                                <h1 className="text-base font-semibold leading-7 text-gray-900 flex items-center">
                                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-[#e9eff1] w-[40px] h-[40px]">
                                        <UsersIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                                    </div>
                                    <div>
                                        <span className="ms-3">{t('Manage Your Users')}</span>
                                        <p className="ms-3 text-sm font-normal leading-6 weight-[200] text-gray-500">
                                            {t('Quickly access and update user details and status')}
                                        </p>
                                    </div>
                                </h1>
                                {/* <div className="order-last flex w-full gap-x-8 text-sm font-semibold leading-6 sm:order-none sm:w-auto sm:border-l sm:border-gray-200 sm:pl-6 sm:leading-7">
                                    <nav aria-label="Tabs" className="flex space-x-4">
                                        {tabs.map(tab => (
                                            <button
                                                key={tab.key}
                                                // href="#"
                                                onClick={() => setActiveTab(tab.key)}
                                                aria-current={tab.key === activeTab ? 'page' : undefined}
                                                className={classNames(
                                                    tab.key === activeTab
                                                        ? 'bg-primary/[.1] text-primary'
                                                        : 'text-gray-500 hover:text-gray-700',
                                                    'rounded-md px-3 py-2 text-sm font-medium'
                                                )}
                                            >
                                                {tab.name}
                                            </button>
                                        ))}
                                    </nav>
                                </div> */}
                            </div>
                            <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap sm:text-end">
                                <button
                                    onClick={() => navigate('/users/user-form')}
                                    type="button"
                                    className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                                >
                                    {t('Add User')}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="mt-8 mb-4 flex sm:flex-row flex-wrap w-full gap-4">
                        <div>
                            <label htmlFor="country" className="block text-sm font-medium leading-6 text-gray-900">
                                {t('Status')}
                            </label>
                            <Select
                                name="active"
                                value={filterObj.active}
                                onChange={e => {
                                    console.log('selected role===', e.target.value)
                                    let value
                                    if (e.target.value === 'true') {
                                        value = true
                                    } else if (e.target.value === 'false') {
                                        value = false
                                    } else {
                                        value = null
                                    }
                                    setFilterObj({ ...filterObj, active: value })
                                }}
                            >
                                <option value={null}>{t('All')}</option>
                                <option value="true">{t('Active')}</option>
                                <option value="false">{t('Inactive')}</option>
                            </Select>
                        </div>
                        <div>
                            <label htmlFor="country" className="block text-sm font-medium leading-6 text-gray-900">
                                {t('User Type')}
                            </label>
                            <Select
                                name="role"
                                value={filterObj.role}
                                onChange={e => {
                                    console.log('selected role===', e.target.value)
                                    setFilterObj({ ...filterObj, role: e.target.value })
                                }}
                            >
                                <option value="">{t('All')}</option>
                                {adminRoles.map(role => (
                                    <option key={role.role_code} value={role.role_code}>
                                        {role.description?.en || role.role_code}
                                    </option>
                                ))}
                            </Select>
                        </div>
                        <div className="flex-2">
                            <label htmlFor="country" className="block text-sm font-medium leading-6 text-gray-900">
                                {t('Search')}
                            </label>
                            <InputGroup>
                                <MagnifyingGlassIcon />
                                <Input
                                    name="search"
                                    id="search"
                                    value={searchString}
                                    onChange={e => setSearchString(e.target.value)}
                                    placeholder="Search users"
                                />
                            </InputGroup>
                        </div>
                        <div className="flex-none grow space-x-2 justify-end place-content-end">
                            <button
                                type="button"
                                onClick={e => {
                                    e.stopPropagation() // Prevent the click event from bubbling up
                                    onClickSearch()
                                }}
                                className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9]"
                            >
                                {t('Search')}
                            </button>
                            <button
                                type="button"
                                onClick={e => {
                                    e.stopPropagation() // Prevent the click event from bubbling up
                                    onClickSearchClear()
                                }}
                                className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                            >
                                {t('Clear')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <Divider />

            {userListLoading && (
                <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                    <img alt="logo" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">Loading, please wait...</p>
                </div>
            )}
            {userListError && (
                <div className="flex justify-center items-center bg-white py-5">
                    <div className="text-lg text-center">{t('Failed to fetch users!')}</div>
                </div>
            )}
            {!userListLoading && !userListError && userList?.length === 0 && (
                <div className="text-center py-24 bg-gray-50 mt-8 rounded-lg">
                    <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        className="mx-auto h-12 w-12 text-gray-400"
                    >
                        <path
                            d="M16.6725 16.6412L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                            strokeWidth={2}
                            vectorEffect="non-scaling-stroke"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('No records found!')}</h3>
                    <p className="mt-1 text-sm text-gray-500">
                        {t('Please add or modify your search criteria and try again.')}
                    </p>
                    <div className="mt-6"></div>
                </div>
            )}
            {!userListLoading && !userListError && userList?.length > 0 && (
                <>
                    <div className="table-custom overflow-x-scroll lg:overflow-hidden rounded-b-lg bg-white p-0 flex">
                        <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
                            <TableHead>
                                <TableRow>
                                    <TableHeader>{t('Photo')}</TableHeader>
                                    <TableHeader>{t('Name')}</TableHeader>
                                    <TableHeader>{t('Email')}</TableHeader>
                                    <TableHeader>{t('Mobile')}</TableHeader>
                                    <TableHeader>{t('User Type')}</TableHeader>
                                    <TableHeader>{t('Status')}</TableHeader>
                                    <TableHeader>{t('Active')}</TableHeader>
                                    <TableHeader>{t('Actions')}</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {userList.map(user => (
                                    <TableRow
                                        key={user.id}
                                        className="cursor-pointer hover:bg-gray-50"
                                        onClick={() => navigate(`/users/user-details/${user.id}`)}
                                        title={`User #${user.id}`}
                                    >
                                        <TableCell>
                                            <ImageDisplay
                                                s3Key={user.additional_info?.profile_photo?.s3_key}
                                                file=""
                                                placeHolderImage={placeHolderImage}
                                                classStyle="w-[50px] h-[50px] sm:w-[30px] sm:h-[30px] rounded-full ring-4 ring-white max-w-none"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex gap-2 items-center">
                                                {/* <img
                    src={user.image.profileImage}
                    alt={`${user.name}'s avatar`}
                    className="h-10 w-10 rounded-full object-cover"
                  /> */}
                                                {user.user_info.given_name} {user.user_info.family_name}
                                            </div>
                                        </TableCell>
                                        <TableCell>{user.email}</TableCell>
                                        <TableCell>{user.user_info.phone_number}</TableCell>
                                        <TableCell>
                                            {user.user_roles.map((role, index) => (
                                                <Badge key={index} color="blue" style={{ marginRight: '8px' }}>
                                                    {role}
                                                </Badge>
                                            ))}
                                        </TableCell>
                                        <TableCell>
                                            <Badge color="lime">{user.cognito_status}</Badge>
                                        </TableCell>
                                        <TableCell>
                                            <Badge color={user.is_active ? 'emerald' : 'rose'}>
                                                {user.is_active ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <Dropdown>
                                                <DropdownButton
                                                    plain
                                                    aria-label="More options"
                                                    onClick={e => e.stopPropagation()}
                                                    className="cursor-pointer"
                                                >
                                                    <EllipsisVerticalIcon />
                                                </DropdownButton>
                                                <DropdownMenu anchor="bottom">
                                                    <DropdownItem
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            navigate(`/users/user-details/${user.id}`)
                                                        }}
                                                    >
                                                        <EyeIcon />
                                                        <DropdownLabel>{t('View')}</DropdownLabel>
                                                    </DropdownItem>
                                                    <DropdownDivider />
                                                    <DropdownItem
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            navigate('/users/user-form', {
                                                                state: {
                                                                    userData: {
                                                                        id: user.id,
                                                                        email: user.email,
                                                                        family_name: user.user_info.family_name,
                                                                        given_name: user.user_info.given_name,
                                                                        locale: user.user_info.locale,
                                                                        phone_number: user.user_info.phone_number,
                                                                        userRoles: user.user_roles,
                                                                        profile_photo:
                                                                            user.additional_info?.profile_photo,
                                                                        additional_info: user.additional_info,
                                                                    },
                                                                },
                                                            })
                                                        }}
                                                    >
                                                        <PencilIcon />
                                                        <DropdownLabel>Edit</DropdownLabel>
                                                    </DropdownItem>
                                                    <DropdownDivider />
                                                    <DropdownItem
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            handleDisableUser(user.id, !user.is_active)
                                                        }}
                                                    >
                                                        <EyeSlashIcon />
                                                        <DropdownLabel>
                                                            {user.is_active ? 'Disable' : 'Enable'}
                                                        </DropdownLabel>
                                                    </DropdownItem>
                                                    {user.cognito_status == 'FORCE_CHANGE_PASSWORD' && (
                                                        <>
                                                            <DropdownDivider />
                                                            <DropdownItem
                                                                onClick={e => {
                                                                    e.stopPropagation()
                                                                    handleResendCode(user.id)
                                                                }}
                                                            >
                                                                <ArrowPathIcon />
                                                                <DropdownLabel>{t('Resend Code')}</DropdownLabel>
                                                            </DropdownItem>
                                                        </>
                                                    )}
                                                </DropdownMenu>
                                            </Dropdown>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    <Divider />
                    <Pagination className="py-6">
                        <PaginationPrevious
                            onClick={() => handlePageChange(page > 0 ? page - 1 : 0)}
                            disabled={page === 0}
                        />
                        <PaginationList>
                            {getPageRange(page, totalPages).map(index => (
                                <PaginationPage
                                    key={index}
                                    current={index === page}
                                    onClick={() => handlePageChange(index)}
                                >
                                    {index + 1}
                                </PaginationPage>
                            ))}
                        </PaginationList>
                        <PaginationNext
                            onClick={() => handlePageChange(page < totalPages - 1 ? page + 1 : totalPages - 1)}
                            disabled={page === totalPages - 1}
                        />
                    </Pagination>
                    <Divider />
                </>
            )}

            {showErrDialog && (
                <ErrorDialog
                    status={true}
                    title="Error"
                    message={errorMessage}
                    onClose={handleErrClose}
                    isMentee={false}
                />
            )}
            {showSucDialog && (
                <SuccessDialog status={true} title="Success" message={successMessage} onClose={handleSucClose} />
            )}

            {showConfirmDialog && (
                <ConfirmDialog
                    status={true}
                    title="User Status Update"
                    negativeActionText="Cancel"
                    positiveActionText="Yes"
                    message={confirmMessage}
                    onPositive={handleConfirmPositive}
                    onNegative={handleConfirmNegative}
                />
            )}
            {(createUserLoading || statusUpdateLoading) && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white"></div>
                </div>
            )}
        </>
    )
}
